import { useState } from 'react'
import { AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react'
import { Button, Input, toast } from '@/components/ui'
import type { DisciplinaryReport } from '../api/types'
import { useUpdateReport } from '../api/queries'

interface Props {
  report: DisciplinaryReport
  /** Unsaved form edits, sent with the issuer signature so issuing can't drop them. */
  pendingEdits?: Record<string, unknown> | null
}

function _fmt(ts: string | null): string {
  if (!ts) return ''
  return new Date(ts).toLocaleString()
}

export function SignOffPanel({ report, pendingEdits }: Props) {
  const update = useUpdateReport(report.id)
  const [issuerName, setIssuerName] = useState(report.issuer_name ?? '')
  const [issuerTitle, setIssuerTitle] = useState(report.issuer_title ?? '')
  const [ackName, setAckName] = useState(report.employee_ack_name ?? '')
  const [refusalReason, setRefusalReason] = useState(report.refusal_reason ?? '')
  const [showRefusalForm, setShowRefusalForm] = useState(false)

  const canIssue = report.status === 'draft'
  const canAcknowledge = report.status === 'issued'

  async function signIssuer() {
    if (!issuerName.trim()) {
      toast.danger('Enter the issuing authority name before signing.')
      return
    }
    try {
      await update.mutateAsync({
        ...(pendingEdits ?? {}),
        action: 'sign_issuer',
        issuer_name: issuerName.trim(),
        issuer_title: issuerTitle.trim() || undefined,
      })
      toast.success(pendingEdits ? 'Changes saved; report issued and signed.' : 'Report issued and signed.')
    } catch {
      toast.danger('Sign-off failed. Nothing was saved and the report was not issued.')
    }
  }

  async function signEmployee() {
    if (!ackName.trim()) {
      toast.danger('Enter the employee name before acknowledging.')
      return
    }
    try {
      await update.mutateAsync({ action: 'sign_employee', employee_ack_name: ackName.trim() })
      toast.success('Employee acknowledgment recorded.')
    } catch {
      toast.danger('Acknowledgment failed. Please try again.')
    }
  }

  async function noteRefusal() {
    try {
      await update.mutateAsync({
        action: 'note_refusal',
        employee_ack_name: ackName.trim() || undefined,
        refusal_reason: refusalReason.trim() || undefined,
      })
      toast.success('Refusal to sign documented.')
      setShowRefusalForm(false)
    } catch {
      toast.danger('Failed to document refusal. Please try again.')
    }
  }

  return (
    <div className="space-y-4">
      {/* Block 1 — Issuing Authority */}
      <div className="rounded-lg border border-border-default p-4 space-y-3">
        <div className="flex items-center gap-2">
          {report.issuer_signed_at ? (
            <CheckCircle size={16} className="text-success" />
          ) : (
            <Clock size={16} className="text-secondary" />
          )}
          <span className="text-sm font-medium">Issuing Authority Sign-off</span>
          {report.issuer_signed_at && (
            <span className="text-xs text-secondary ml-auto">{_fmt(report.issuer_signed_at)}</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-secondary uppercase tracking-wide">Name</label>
            <Input
              value={issuerName}
              onChange={(e) => setIssuerName(e.target.value)}
              placeholder="Full name"
              disabled={Boolean(report.issuer_signed_at) || !canIssue}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-secondary uppercase tracking-wide">Title / Role</label>
            <Input
              value={issuerTitle}
              onChange={(e) => setIssuerTitle(e.target.value)}
              placeholder="e.g. HR Manager"
              disabled={Boolean(report.issuer_signed_at) || !canIssue}
            />
          </div>
        </div>
        {canIssue && !report.issuer_signed_at && (
          <div className="flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={signIssuer}
              loading={update.isPending}
            >
              {pendingEdits ? 'Save & Issue Report' : 'Sign & Issue Report'}
            </Button>
          </div>
        )}
        {!canIssue && !report.issuer_signed_at && (
          <p className="text-xs text-secondary">Save the report first to enable sign-off.</p>
        )}
      </div>

      {/* Block 2 — Employee Acknowledgment */}
      <div className="rounded-lg border border-border-default p-4 space-y-3">
        <div className="flex items-center gap-2">
          {report.employee_ack_at && !report.employee_refused_to_sign && (
            <CheckCircle size={16} className="text-success" />
          )}
          {report.employee_refused_to_sign && (
            <XCircle size={16} className="text-danger" />
          )}
          {!report.employee_ack_at && (
            <Clock size={16} className="text-secondary" />
          )}
          <span className="text-sm font-medium">Employee Acknowledgment</span>
          {report.employee_ack_at && (
            <span className="text-xs text-secondary ml-auto">{_fmt(report.employee_ack_at)}</span>
          )}
        </div>

        {report.employee_refused_to_sign && (
          <div className="flex items-start gap-2 rounded bg-danger-subtle p-2">
            <AlertTriangle size={14} className="text-danger mt-0.5 shrink-0" />
            <div className="text-xs text-danger">
              <strong>Refused to sign</strong>
              {report.refusal_reason && ` — ${report.refusal_reason}`}
            </div>
          </div>
        )}

        <div className="space-y-1">
          <label className="text-xs text-secondary uppercase tracking-wide">Employee Name</label>
          <Input
            value={ackName}
            onChange={(e) => setAckName(e.target.value)}
            placeholder="Employee full name"
            disabled={Boolean(report.employee_ack_at) || !canAcknowledge}
          />
        </div>

        {canAcknowledge && !report.employee_ack_at && !showRefusalForm && (
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRefusalForm(true)}
            >
              Refused to Sign
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={signEmployee}
              loading={update.isPending}
            >
              Employee Acknowledges
            </Button>
          </div>
        )}

        {showRefusalForm && (
          <div className="space-y-2 rounded border border-danger/30 bg-danger-subtle/50 p-3">
            <p className="text-xs text-danger font-medium">Document Employee Refusal to Sign</p>
            <div className="space-y-1">
              <label className="text-xs text-secondary uppercase tracking-wide">
                Reason (optional)
              </label>
              <Input
                value={refusalReason}
                onChange={(e) => setRefusalReason(e.target.value)}
                placeholder="Reason employee refused to sign"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRefusalForm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={noteRefusal}
                loading={update.isPending}
              >
                Document Refusal
              </Button>
            </div>
          </div>
        )}

        {!canAcknowledge && !report.employee_ack_at && (
          <p className="text-xs text-secondary">
            {report.status === 'draft'
              ? 'Issue the report (Issuing Authority sign-off) to enable employee acknowledgment.'
              : ''}
          </p>
        )}
      </div>

      {report.status === 'acknowledged' && !report.employee_refused_to_sign && (
        <p className="text-xs text-success text-center">
          ✓ Report issued and acknowledged by employee
        </p>
      )}
      {report.status === 'acknowledged' && report.employee_refused_to_sign && (
        <p className="text-xs text-danger text-center">
          Report issued — employee refused to sign. Refusal documented.
        </p>
      )}
    </div>
  )
}
